// ============================================================
// COVRFLY® — Selector de Modelos Premium
// Versión: 1.3.5
// Fecha: 2026-02-05
//
// Base: v1.3.4 (9 fixes de auditoría v1.3.3)
// Fixes v1.3.5 (auditoría cruzada GPT + Gemini de v1.3.4):
//   #1 — Doble open: quitar role="button", tabindex, keydown del wrapper
//   #2 — Wrapper semántica: solo brandBtn/modelBtn son interactivos
//   #3 — cleanup overflow: usar initialBodyOverflow (baseline al inicio)
//   #4 — .startsWith → .includes: más robusto para "Samsung Galaxy..."
//   #5 — Focus trap: Tab/Shift+Tab cíclico dentro del modal
//   #6 — Remover prevTab: variable muerta
//   #7 — constant() iOS: fallback para iOS < 11.2
//   #8 — Quick View: buscar variant-selects visible si hay múltiples
// ============================================================

(function () {
  'use strict';

  const LOG = (...args) => console.log('[COVRFLY]', ...args);
  LOG('Iniciando selector de modelos v1.3.5...');

  // ============================================================
  // 1. CLEANUP — Patrón window.__covrfly (Fix #1 de v1.3.4)
  // ============================================================
  if (window.__covrfly && typeof window.__covrfly.cleanup === 'function') {
    window.__covrfly.cleanup();
    LOG('Instancia previa limpiada via cleanup()');
  }

  // Limpieza de DOM residual (safety net)
  const CLEANUP_SELECTORS = [
    '#covrfly-styles',
    '#covrfly-trigger-wrap',
    '#covrfly-overlay',
    '#covrfly-modal'
  ];
  CLEANUP_SELECTORS.forEach(sel => {
    const el = document.querySelector(sel);
    if (el) {
      el.remove();
      LOG('DOM limpiado:', sel);
    }
  });

  // ============================================================
  // 2. CONFIGURACIÓN DE MODELOS
  // ============================================================
  const MODELS = {
    apple: [
      'iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone 17 Air', 'iPhone 17',
      'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16',
      'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
      'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
      'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13',
      'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12',
      'iPhone 11 Pro Max', 'iPhone 11 Pro'
    ],
    samsung: [
      'Galaxy S25 Ultra', 'Galaxy S25+', 'Galaxy S25',
      'Galaxy S24 Ultra', 'Galaxy S24+', 'Galaxy S24',
      'Galaxy S23 Ultra', 'Galaxy S23+', 'Galaxy S23'
    ]
  };

  const BRAND_LABELS = { apple: 'Apple', samsung: 'Samsung' };

  const DEFAULT_MODELS = {
    apple: 'iPhone 17 Pro Max',
    samsung: 'Galaxy S25 Ultra'
  };

  // Fix #4 v1.3.5 — .includes() en vez de .startsWith() para mayor robustez
  // Soporta "Samsung Galaxy S25" además de "Galaxy S25"
  function getBrandFromModel(model) {
    if (!model) return 'apple';
    const lower = model.toLowerCase();
    if (lower.includes('galaxy') || lower.includes('samsung')) return 'samsung';
    if (lower.includes('iphone') || lower.includes('apple')) return 'apple';
    LOG('⚠️ Modelo no reconocido en lista:', model, '→ fallback a Apple');
    return 'apple';
  }

  // ============================================================
  // 3. ENCONTRAR ELEMENTOS DEL DOM
  // ============================================================

  // Fix #8 v1.3.5 — Buscar variant-selects visible si hay múltiples (Quick View)
  function findVisibleVariantSelects() {
    const allSelects = document.querySelectorAll('variant-selects');
    if (allSelects.length === 0) return null;
    if (allSelects.length === 1) return allSelects[0];
    
    // Si hay múltiples, buscar el visible
    for (const vs of allSelects) {
      if (vs.offsetParent !== null) {
        LOG('Múltiples variant-selects encontrados, usando el visible');
        return vs;
      }
    }
    // Fallback al primero si ninguno tiene offsetParent
    return allSelects[0];
  }

  const variantSelects = findVisibleVariantSelects();
  if (!variantSelects) {
    LOG('ERROR: No se encontró variant-selects');
    return;
  }

  const modelSelect = variantSelects.querySelector('select[name="options[Elige tu Modelo]"]')
    || variantSelects.querySelector('select[name="options[Modelo]"]')
    || document.querySelector('select[name="options[Elige tu Modelo]"]')
    || document.querySelector('select[name="options[Modelo]"]');
  if (!modelSelect) {
    LOG('ERROR: No se encontró select de Modelo');
    return;
  }

  const selectContainer = modelSelect.closest('.product-form__input');
  LOG('Elementos encontrados ✓');

  // ============================================================
  // 4. INYECTAR ESTILOS
  // ============================================================
  const styles = document.createElement('style');
  styles.id = 'covrfly-styles';
  styles.textContent = `
    /* === CSS Variables === */
    :root {
      --cf-black: #000;
      --cf-white: #fff;
      --cf-gray-50: #fafafa;
      --cf-gray-100: #f5f5f5;
      --cf-gray-200: #e5e5e5;
      --cf-gray-300: #d4d4d4;
      --cf-gray-400: #a3a3a3;
      --cf-gray-500: #737373;
      --cf-gray-600: #525252;
      --cf-radius-card: 16px;
      --cf-radius-pill: 9999px;
      --cf-radius-modal: 24px;
      --cf-transition: 0.3s ease-out;
      --cf-z-overlay: 9998;
      --cf-z-modal: 9999;
    }

    /* === Trigger dividido [Marca | Modelo] === */
    /* Fix #1/#2 v1.3.5 — wrapper NO es interactivo, solo los botones */
    #covrfly-trigger-wrap {
      display: flex;
      align-items: stretch;
      border: 1.5px solid var(--cf-gray-300);
      border-radius: var(--cf-radius-card);
      overflow: hidden;
      margin-bottom: 16px;
      background: var(--cf-white);
    }

    .cf-trigger-brand,
    .cf-trigger-model {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 14px 16px;
      background: transparent;
      border: none;
      font-family: inherit;
      font-size: 16px;
      font-weight: 500;
      color: var(--cf-black);
      cursor: pointer;
    }

    .cf-trigger-brand {
      min-width: 110px;
    }

    .cf-trigger-brand:hover,
    .cf-trigger-model:hover {
      background: var(--cf-gray-50);
    }

    /* Línea divisoria premium — no toca bordes */
    .cf-trigger-divider {
      width: 1px;
      background: var(--cf-gray-200);
      margin: 8px 0;
    }

    .cf-trigger-model {
      flex: 1;
      text-align: left;
    }

    .cf-trigger-model-text {
      color: var(--cf-gray-500);
    }

    .cf-trigger-model-text.has-selection {
      color: var(--cf-black);
    }

    /* Chevron SVG */
    .cf-chevron {
      width: 14px;
      height: 14px;
      color: var(--cf-gray-400);
      flex-shrink: 0;
    }

    /* === Overlay === */
    #covrfly-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      z-index: var(--cf-z-overlay);
      opacity: 0;
      visibility: hidden;
      transition: opacity var(--cf-transition), visibility var(--cf-transition);
      touch-action: none;
      -webkit-tap-highlight-color: transparent;
    }

    #covrfly-overlay.is-visible {
      opacity: 1;
      visibility: visible;
    }

    /* === Modal (Bottom Sheet) === */
    #covrfly-modal {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      max-height: 70vh;
      background: var(--cf-white);
      border-radius: var(--cf-radius-modal) var(--cf-radius-modal) 0 0;
      z-index: var(--cf-z-modal);
      display: flex;
      flex-direction: column;
      transform: translateY(100%);
      transition: transform var(--cf-transition);
      /* Fix #7 v1.3.5 — constant() para iOS < 11.2, luego env() */
      padding-bottom: constant(safe-area-inset-bottom);
      padding-bottom: env(safe-area-inset-bottom, 0);
      outline: none;
    }

    #covrfly-modal.is-visible {
      transform: translateY(0);
    }

    /* Handle */
    .cf-handle {
      width: 36px;
      height: 4px;
      background: var(--cf-gray-300);
      border-radius: 2px;
      margin: 10px auto 0;
    }

    /* Title */
    .cf-modal-title {
      text-align: center;
      font-size: 18px;
      font-weight: 600;
      padding: 12px 20px 0;
      color: var(--cf-black);
    }

    /* Tabs */
    .cf-tabs {
      display: flex;
      justify-content: center;
      gap: 8px;
      padding: 16px 20px;
    }

    .cf-tab {
      padding: 8px 24px;
      border-radius: var(--cf-radius-pill);
      border: none;
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background var(--cf-transition), color var(--cf-transition);
    }

    .cf-tab.is-active {
      background: var(--cf-black);
      color: var(--cf-white);
    }

    .cf-tab:not(.is-active) {
      background: var(--cf-gray-100);
      color: var(--cf-black);
    }

    .cf-tab:not(.is-active):hover {
      background: var(--cf-gray-200);
    }

    /* Model List */
    .cf-list {
      flex: 1;
      overflow-y: auto;
      padding: 0 20px 20px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }

    /* Model Card */
    .cf-card {
      padding: 16px;
      border: 1px solid var(--cf-gray-200);
      border-radius: var(--cf-radius-card);
      background: var(--cf-white);
      font-family: inherit;
      font-size: 15px;
      font-weight: 500;
      color: var(--cf-black);
      cursor: pointer;
      transition: border-color var(--cf-transition), box-shadow var(--cf-transition);
      text-align: left;
    }

    .cf-card:hover {
      border-color: var(--cf-gray-400);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .cf-card.is-selected {
      border: 2px solid var(--cf-black);
      padding: 15px; /* compensar borde */
    }

    /* Ocultar select original */
    .cf-hidden {
      display: none !important;
    }
  `;
  document.head.appendChild(styles);
  LOG('Estilos inyectados');

  // ============================================================
  // 5. ESTADO
  // ============================================================
  let selectedModel = modelSelect.value || null;
  let activeTab = getBrandFromModel(selectedModel);


  // Fix #6 v1.3.5 — Removido prevTab (variable muerta)
  
  // Fix #3 v1.3.5 — Guardar overflow baseline al inicio del IIFE
  const initialBodyOverflow = document.body.style.overflow;
  let prevBodyOverflow = initialBodyOverflow;

  // ============================================================
  // 6. CREAR HTML
  // ============================================================

  // SVG Chevron reutilizable
  const chevronSVG = `<svg class="cf-chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 8 10 12 14 8"/></svg>`;

  // Fix #1/#2 v1.3.5 — Wrapper sin role, tabindex, ni keydown
  // Solo es un contenedor visual, la interactividad está en los botones
  const triggerWrap = document.createElement('div');
  triggerWrap.id = 'covrfly-trigger-wrap';
  // NO role="button", NO tabindex, NO aria-haspopup aquí
  triggerWrap.innerHTML = `
    <button type="button" class="cf-trigger-brand" id="cf-trigger-brand">
      <span id="cf-brand-text">${BRAND_LABELS[activeTab]}</span>
      ${chevronSVG}
    </button>
    <div class="cf-trigger-divider"></div>
    <button type="button" class="cf-trigger-model" id="cf-trigger-model" aria-haspopup="dialog" aria-expanded="false">
      <span id="cf-model-text" class="cf-trigger-model-text ${selectedModel ? 'has-selection' : ''}">${selectedModel || 'Elige tu modelo'}</span>
      ${chevronSVG}
    </button>
  `;

  // Overlay
  const overlay = document.createElement('div');
  overlay.id = 'covrfly-overlay';

  // Modal
  const modal = document.createElement('div');
  modal.id = 'covrfly-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Seleccionar modelo de dispositivo');
  modal.setAttribute('tabindex', '-1');
  modal.innerHTML = `
    <div class="cf-handle"></div>
    <div class="cf-modal-title">Modelo de dispositivo</div>
    <div class="cf-tabs" role="tablist">
      <button type="button" class="cf-tab ${activeTab === 'apple' ? 'is-active' : ''}" data-tab="apple" role="tab" aria-selected="${activeTab === 'apple'}">Apple</button>
      <button type="button" class="cf-tab ${activeTab === 'samsung' ? 'is-active' : ''}" data-tab="samsung" role="tab" aria-selected="${activeTab === 'samsung'}">Samsung</button>
    </div>
    <div class="cf-list" id="cf-list" role="listbox" aria-label="Modelos disponibles"></div>
  `;

  // Inyectar en DOM
  variantSelects.insertAdjacentElement('beforebegin', triggerWrap);
  document.body.appendChild(overlay);
  document.body.appendChild(modal);

  // Ocultar select original
  if (selectContainer) {
    selectContainer.classList.add('cf-hidden');
    LOG('Select original ocultado');
  }

  // ============================================================
  // 7. REFERENCIAS A ELEMENTOS
  // ============================================================
  const brandBtn = triggerWrap.querySelector('#cf-trigger-brand');
  const modelBtn = triggerWrap.querySelector('#cf-trigger-model');
  const brandText = triggerWrap.querySelector('#cf-brand-text');
  const modelText = triggerWrap.querySelector('#cf-model-text');
  const list = modal.querySelector('#cf-list');
  const tabs = modal.querySelectorAll('.cf-tab');

  // Focus management
  let lastFocus = null;

  // ============================================================
  // 8. FUNCIONES
  // ============================================================

  function renderModels() {
    const models = MODELS[activeTab];
    list.innerHTML = models.map(model => `
      <button type="button"
              class="cf-card ${model === selectedModel ? 'is-selected' : ''}"
              data-model="${model}"
              role="option"
              aria-selected="${model === selectedModel}">
        ${model}
      </button>
    `).join('');
  }

  function isModalOpen() {
    return modal.classList.contains('is-visible');
  }

  // Fix #5 v1.3.5 — Focus trap: obtener elementos focuseables del modal
  function getFocusableElements() {
    return modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
  }

  function openModal() {
    lastFocus = document.activeElement;

    renderModels();
    overlay.classList.add('is-visible');
    modal.classList.add('is-visible');
    modelBtn.setAttribute('aria-expanded', 'true');

    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('cf-model-selector-open');

    modal.focus();

    // Double RAF para scroll confiable
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const selected = list.querySelector('.cf-card.is-selected');
        if (selected) {
          selected.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      });
    });

    LOG('Modal abierto');
  }

  function closeModal(autoSelectDefault = false) {
    overlay.classList.remove('is-visible');
    modal.classList.remove('is-visible');
    modelBtn.setAttribute('aria-expanded', 'false');

    document.body.style.overflow = prevBodyOverflow;
    document.body.classList.remove('cf-model-selector-open');

    // Auto-selección: si cambió de tab sin seleccionar modelo de esa marca
    if (autoSelectDefault && activeTab !== getBrandFromModel(selectedModel)) {
      const defaultModel = DEFAULT_MODELS[activeTab];
      if (defaultModel) {
        LOG('Auto-selección al cerrar:', defaultModel);
        selectModel(defaultModel);
        return;
      }
    }

    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }

    LOG('Modal cerrado');
  }

  function selectModel(model) {
    selectedModel = model;
    activeTab = getBrandFromModel(model);

    modelSelect.value = model;
    modelSelect.dispatchEvent(new Event('change', { bubbles: true }));

    brandText.textContent = BRAND_LABELS[activeTab];
    modelText.textContent = model;
    modelText.classList.add('has-selection');

    tabs.forEach(t => {
      const isActive = t.dataset.tab === activeTab;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive);
    });

    LOG('Modelo seleccionado:', model);

    overlay.classList.remove('is-visible');
    modal.classList.remove('is-visible');
    modelBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = prevBodyOverflow;

    modelBtn.focus();
  }

  function switchTab(tab) {
    activeTab = tab;

    tabs.forEach(t => {
      const isActive = t.dataset.tab === tab;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive);
    });

    brandText.textContent = BRAND_LABELS[tab];

    renderModels();
    LOG('Tab:', tab);
  }

  // ============================================================
  // 9. EVENT LISTENERS
  // ============================================================

  function handleBrandClick() { openModal(); }
  function handleModelClick() { openModal(); }
  function handleOverlayClick() { closeModal(true); }

  // Fix #5 v1.3.5 — Focus trap implementado
  function handleKeydown(e) {
    if (!isModalOpen()) return;

    // Escape cierra
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal(true);
      return;
    }

    // Tab / Shift+Tab — focus trap
    if (e.key === 'Tab') {
      const focusableEls = getFocusableElements();
      if (focusableEls.length === 0) return;

      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstEl || document.activeElement === modal) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
  }

  // Fix #1/#2 v1.3.5 — Removido handleTriggerKeydown del wrapper
  // Los botones manejan Enter/Space nativamente

  function handleTabClick(e) {
    const tab = e.currentTarget.dataset.tab;
    if (tab) switchTab(tab);
  }

  function handleListClick(e) {
    const card = e.target.closest('.cf-card');
    if (card) {
      selectModel(card.dataset.model);
    }
  }

  function handleExternalChange() {
    const newValue = modelSelect.value;
    if (newValue && newValue !== selectedModel) {
      selectedModel = newValue;
      activeTab = getBrandFromModel(newValue);

      brandText.textContent = BRAND_LABELS[activeTab];
      modelText.textContent = newValue;
      modelText.classList.add('has-selection');

      tabs.forEach(t => {
        const isActive = t.dataset.tab === activeTab;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive);
      });

      if (isModalOpen()) {
        renderModels();
        LOG('Modal re-renderizado por cambio externo');
      }

      LOG('Sync externo:', newValue);
    }
  }

  // Registrar listeners
  brandBtn.addEventListener('click', handleBrandClick);
  modelBtn.addEventListener('click', handleModelClick);
  // Fix #1/#2 v1.3.5 — NO hay listener en triggerWrap
  overlay.addEventListener('click', handleOverlayClick);
  document.addEventListener('keydown', handleKeydown);

  tabs.forEach(tab => {
    tab.addEventListener('click', handleTabClick);
  });

  list.addEventListener('click', handleListClick);
  modelSelect.addEventListener('change', handleExternalChange);

  // ============================================================
  // 10. CLEANUP FUNCTION
  // ============================================================
  function cleanup() {
    LOG('Ejecutando cleanup...');

    document.removeEventListener('keydown', handleKeydown);
    modelSelect.removeEventListener('change', handleExternalChange);

    brandBtn.removeEventListener('click', handleBrandClick);
    modelBtn.removeEventListener('click', handleModelClick);
    overlay.removeEventListener('click', handleOverlayClick);
    tabs.forEach(tab => tab.removeEventListener('click', handleTabClick));
    list.removeEventListener('click', handleListClick);

    // Fix #3 v1.3.5 — Usar initialBodyOverflow (baseline) en cleanup
    document.body.style.overflow = initialBodyOverflow;

    CLEANUP_SELECTORS.forEach(sel => {
      const el = document.querySelector(sel);
      if (el) el.remove();
    });

    if (selectContainer) {
      selectContainer.classList.remove('cf-hidden');
    }

    LOG('Cleanup completo ✓');
  }

  // Registrar en window
  window.__covrfly = {
    cleanup,
    version: '1.3.5',
    getState: () => ({ selectedModel, activeTab, isOpen: isModalOpen() })
  };

  // ============================================================
  // 11. INICIALIZACIÓN
  // ============================================================
  LOG('✅ Selector v1.3.5 inicializado');
  LOG('Estado:', { selectedModel, activeTab });
  LOG('Fixes aplicados: doble-open, focus-trap, .includes, constant(), Quick View');

  // Anti-FOUC v2.0: señal al orquestador
  window.cfPdpDone && window.cfPdpDone('model');
  LOG('📡 Anti-FOUC v2.0: model done');

})();




